import { useEffect, useRef } from 'react';
import { Terminal } from 'lucide-react';

interface ConsoleProps {
  logs: Array<{
    timestamp: string;
    level: string;
    message: string;
    details?: any;
  }>;
  maxHeight?: string;
  className?: string;
}

export function Console({ logs, maxHeight = '400px', className = '' }: ConsoleProps) {
  const consoleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (consoleRef.current) {
      consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
    }
  }, [logs]);

  const getLogColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'error':
        return 'text-red-500';
      case 'warn':
        return 'text-yellow-500';
      case 'info':
        return 'text-blue-500';
      default:
        return 'text-gray-300';
    }
  };

  return (
    <div className={`bg-gray-900 rounded-lg ${className}`}>
      <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-800">
        <Terminal className="w-4 h-4 text-gray-400" />
        <span className="text-sm font-medium text-gray-300">Console</span>
      </div>
      <div
        ref={consoleRef}
        className="p-4 font-mono text-sm overflow-auto whitespace-pre-wrap"
        style={{ maxHeight, minHeight: '200px' }}
      >
        {logs.map((log, index) => (
          <div key={index} className="mb-2">
            <span className="text-gray-500">[{new Date(log.timestamp).toLocaleTimeString()}] </span>
            <span className={getLogColor(log.level)}>{log.level.toUpperCase()}: </span>
            <span className="text-gray-300">{log.message}</span>
            {log.details && typeof log.details === 'object' && (
              <div className="mt-1 ml-8">
                {Object.entries(log.details as Record<string, unknown>).map(([key, value]) => {
                  // Skip source field since it's used for filtering
                  if (key === 'source') return null;
                  
                  // Handle nested details object
                  if (key === 'details' && value && typeof value === 'object') {
                    return Object.entries(value as Record<string, unknown>).map(([detailKey, detailValue]) => (
                      <div key={detailKey} className="text-gray-400 text-xs">
                        <span className="text-gray-500">{detailKey}:</span>{' '}
                        {detailValue && typeof detailValue === 'object'
                          ? JSON.stringify(detailValue, null, 2)
                          : String(detailValue)
                        }
                      </div>
                    ));
                  }
                  
                  // Handle other fields
                  return (
                    <div key={key} className="text-gray-400 text-xs">
                      <span className="text-gray-500">{key}:</span>{' '}
                      {value && typeof value === 'object'
                        ? JSON.stringify(value, null, 2)
                        : String(value)
                      }
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
